import React, { useEffect, useRef } from 'react';
import { createChart, ColorType, IChartApi, AreaSeries } from 'lightweight-charts';
import ChartShareButton from '../Common/ChartShareButton';
import { useTheme } from '../../context/ThemeContext';

interface PortfolioHistoryPoint {
    date: string;
    value: number;
}

interface Props {
    data: PortfolioHistoryPoint[];
    height?: number;
    /** 'fcfa' : valeur en francs CFA (défaut) ; 'pct' : performance cumulée en %. */
    format?: 'fcfa' | 'pct';
}

// Couleurs lues sur le thème courant : des gris codés en dur disparaissaient en mode sombre.
const couleurTheme = (variable: string, repli: string): string =>
    getComputedStyle(document.documentElement).getPropertyValue(variable).trim() || repli;

const PortfolioEvolutionChart: React.FC<Props> = ({ data, height = 300, format = 'fcfa' }) => {
    const chartContainerRef = useRef<HTMLDivElement>(null);
    const chartRef = useRef<IChartApi | null>(null);
    const { theme } = useTheme();

    useEffect(() => {
        if (!chartContainerRef.current) return;

        const chart = createChart(chartContainerRef.current, {
            layout: {
                background: { type: ColorType.Solid, color: 'transparent' },
                textColor: couleurTheme('--text-muted', '#64748b'),
            },
            localization: {
                locale: 'fr-FR',
                priceFormatter: (p: number) =>
                    format === 'pct'
                        ? `${p > 0 ? '+' : ''}${p.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} %`
                        : Math.round(p).toLocaleString('fr-FR'),
            },
            width: chartContainerRef.current.clientWidth,
            height: height,
            grid: {
                vertLines: { color: couleurTheme('--border-color', '#e2e8f0') },
                horzLines: { color: couleurTheme('--border-color', '#e2e8f0') },
            },
            rightPriceScale: {
                borderVisible: false,
            },
            timeScale: {
                borderVisible: false,
            },
        });

        const areaSeries = chart.addSeries(AreaSeries, {
            lineColor: '#2563eb',
            topColor: 'rgba(37, 99, 235, 0.4)',
            bottomColor: 'rgba(37, 99, 235, 0.0)',
            lineWidth: 2,
        });

        // Format data for lightweight-charts (needs sorted dates)
        // Assuming data is already sorted from the hook, but good to ensure uniqueness
        const chartData = data.map((d) => ({
            time: d.date,
            value: d.value,
        }));

        // Remove duplicates if any (lightweight-charts strictness)
        const uniqueData = chartData.filter(
            (v, i, a) => a.findIndex((t) => t.time === v.time) === i
        );

        areaSeries.setData(uniqueData);

        chart.timeScale().fitContent();

        chartRef.current = chart;

        const handleResize = () => {
            if (chartContainerRef.current && chartRef.current) {
                chartRef.current.applyOptions({ width: chartContainerRef.current.clientWidth });
            }
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            chart.remove();
        };
    }, [data, height, format, theme]);

    return (
        <div style={{ position: 'relative', width: '100%', height: `${height}px` }}>
            <div ref={chartContainerRef} style={{ width: '100%', height: '100%' }} />
            <ChartShareButton
                targetRef={chartContainerRef}
                fileName="portfolio_evolution"
                style={{ position: 'absolute', top: 10, right: 60, zIndex: 10 }}
            />
        </div>
    );
};

export default PortfolioEvolutionChart;
